import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import {FormLabel, Grid, IconButton} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import _, {isEmpty} from 'lodash';
import AgyleTextField from '../../components/ui/fields/agyleTextField';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import AgyleDictionaryField from '../../components/ui/selector/agyleDictionaryField';
import {useDebounce} from 'use-debounce';
import API from '../../app/api';
import parse from 'autosuggest-highlight/parse';
import {FixedSizeList as List} from 'react-window';
import uuidv4 from 'uuid/v4';
import moment from 'moment/moment';
import useOpenState from '../../hooks/useOpenState';
import MessageBox from '../../util/messageBox';
import {useSelector} from 'react-redux';
import {user} from '../../store/reducers/user';
import Formatter from '../../util/formatter';
import {escapeRegExp} from '../../pages/ward/forms/formsUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#333333',
        boxShadow: `0px 0px ${theme.palette.mode === 'light' ? 6 : 0}px 0px rgba(0, 0, 0, 0.25)`,
        borderRadius: 5,
        padding: 8,
    },
    avatarCustom: {
        width: 'auto !important',
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 15,
        backgroundColor: theme.palette.background.paper,
        color: '#fff',
    },
    code: {
        display: 'inline',
        fontSize: '80%',
        fontWeight: 'bold',
        backgroundColor: theme.palette.background.default,
        padding: '3px 8px',
        marginRight: 10,
        borderRadius: 4,
    },
    description: {
        display: 'inline',
    },
    label: {
        color: theme.palette.mode === 'light' ? '#626466' : '#FFFFFF',
        fontWeight: 'bold',
        fontSize: 14,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 10,
    },
    inputRoot: {
        minWidth: '100%',
    },
    warning: {
        fontSize: 'small',
        fontWeight: 'bold',
        color: '#E25B61',
    },
}));

const MULTI_SEARCH_COLUMN_NAME = ['altCode', 'description'];

const ICd10Control = ({value, onChange, disabled}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    const controller = new AbortController();

    // User
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // autocomplete states
    const [autocompleteOpen, setAutocompleteOpen] = React.useState(false);
    const [autocompleteOptions, setAutocompleteOptions] = React.useState([]);
    const [autocompleteInputValue, setAutocompleteInputValue] = React.useState('');
    const [autocompleteLoading, setAutocompleteLoading] = React.useState(false);
    const [focus, setFocus] = useState();

    const [autocompleteInputValueDebounced] = useDebounce(autocompleteInputValue, 250);
    const [innerValue, setInnerValue] = useState([]);
    const [orgValue, setOrgValue] = useState([]);
    const firstUpdate = useRef(true);

    // Confirm delete states
    const [tempNewVal, setTempNewVal] = React.useState(null);
    const removeType = useRef(null);
    const [isConfirmRemoveElementOpen, openConfirmRemoveElement, closeConfirmRemoveElement] = useOpenState(false, true);

    const [showWarning, setShowWarning] = useState(false);

    // Abort requests
    useEffect(() => {
        const clone = _.cloneDeep(value) || [];
        setOrgValue(clone);
        setInnerValue(clone);
        return () => {
            controller.abort();
        };
    }, []);

    // Fetch Data
    useEffect(() => {
        let isMounted = true;

        if (autocompleteInputValueDebounced && !_.isEmpty(autocompleteInputValueDebounced)) {
            if (_.trim(autocompleteInputValueDebounced)?.length < 3) {
                setShowWarning(true);
            } else {
                setShowWarning(false);
                let filter = '';

                filter = getFilter(autocompleteInputValueDebounced, filter);

                setAutocompleteLoading(true);
                API.getDictByURL(getDictionaryURL(filter), controller.signal)
                    .then(dictData => {
                        if (!isMounted) return;

                        if (dictData?.__data && !_.isEmpty(dictData.__data)) {
                            let data = dictData.__data;
                            setAutocompleteOptions(data);
                        } else {
                            setAutocompleteOptions([]);
                        }
                    })
                    .catch(function (error) {
                        console.error('Autocomplete dictionaries error', error);
                    })
                    .finally(() => {
                        setAutocompleteLoading(false);
                    });
            }
        }

        return () => {
            isMounted = false;
        };
    }, [autocompleteInputValueDebounced]);

    // Save changes
    useEffect(() => {
        if (!firstUpdate.current) {
            if (!_.isEqual(innerValue, orgValue)) {
                onChange(innerValue);
                setOrgValue(innerValue);
            }
        }
    }, [innerValue]);

    // Clear warning
    useEffect(() => {
        if (_.isEmpty(autocompleteInputValue)) setShowWarning(false);
    }, [autocompleteInputValue]);

    const getDictionaryURL = filter => {
        return `/$dict/ICD10?$filter=${encodeURIComponent(filter)}`;
    };

    // Get input search
    function getFilter(input, filter) {
        if (!_.isEmpty(input)) {
            if (!_.isEmpty(filter)) filter += ' AND ';
            for (let i = 0; i < MULTI_SEARCH_COLUMN_NAME?.length; i++) {
                if (i > 0) {
                    filter += ' or ';
                }
                filter += `${MULTI_SEARCH_COLUMN_NAME[i]} regex '.*` + escapeRegExp(input) + `.*'`;
            }
        }
        return filter;
    }

    const openAutocomplete = () => {
        if (!autocompleteOpen) {
            setAutocompleteOpen(true);
            setTimeout(() => {
                focus?.focus();
            }, 10);
        }
    };

    const handleLeave = (e) => {
        if (autocompleteOpen && e?.pointerType !== 'touch') setAutocompleteOpen(false);
    };

    const handleAutocompleteState = (val) => {
        setAutocompleteOpen(val);
    };

    const handleChangeValue = (newValue, customId) => {
        let clone = [];
        const id = customId || uuidv4() + '';
        clone = newValue.slice().filter(element => !element.__id);
        let filterNewEntry = _.cloneDeep(newValue);
        filterNewEntry = filterNewEntry.filter(element => element.__id);

        if (filterNewEntry && filterNewEntry.length > 0) {
            let data = {
                id,
                value: filterNewEntry[0],
                requestedTimestamp: moment().toISOString(),
                who: tester,
                login: userObject?.userName,
            };
            clone.push(data);
        }

        firstUpdate.current = false;
        setInnerValue(clone);
    };

    const handleChange = (_event, newValues, reason) => {
        const id = uuidv4() + '';
        if (reason === 'removeOption' || reason === 'clear') {
            setTempNewVal(newValues);
            openConfirmRemoveElement();
            removeType.current = reason;
        } else {
            handleChangeValue(newValues, id);
        }
        setAutocompleteOpen(false);
    };

    const renderAvatar = (option) => {
        return (
            <div
                style={{
                    lineHeight: '24px',
                    color: '#fff',
                    fontWeight: 'normal',
                }}
                className={classes.avatarCustom}
            >
                {option?.value?.altCode}
            </div>
        );
    };

    const createParts = optionValue => {
        let matches = [];
        if (optionValue && autocompleteInputValueDebounced && !_.isEmpty(autocompleteInputValueDebounced)) {
            let idx = optionValue.toLowerCase().indexOf(autocompleteInputValueDebounced.toLowerCase());
            while (idx >= 0) {
                matches.push([idx, idx + autocompleteInputValueDebounced.length]);
                idx = optionValue.toLowerCase().indexOf(autocompleteInputValueDebounced.toLowerCase(), idx + autocompleteInputValueDebounced.length);
            }
        }
        return parse(optionValue, matches);
    };

    const renderLocalOption = (option, props) => {
        const parts = createParts(option.__description);

        return (
            <li {...props}>
                <Grid container direction="row">
                    <div className={classes.code}>
                        {option.altCode}
                    </div>

                    <div className={classes.description}>
                        {parts.map((part, index) => (
                            <span key={index} style={{
                                textDecoration: part.highlight ? 'underline' : 'none',
                                fontWeight: part.highlight ? 700 : 400,
                            }}>
                            {part.text}
                        </span>
                        ))}
                    </div>
                </Grid>
            </li>
        );
    };

    const handleCloseConfirmRemove = (e) => {
        closeConfirmRemoveElement(e);
        setTempNewVal(null);
        removeType.current = null;
    };

    const handleRemove = () => {
        closeConfirmRemoveElement();
        handleChangeValue(tempNewVal);
        setTempNewVal(null);
        removeType.current = null;
    };

    return (
        <>
            <div className={classes.root} onPointerLeave={handleLeave}>
                <div
                    className={classes.divRow}
                    onClick={openAutocomplete}
                >
                    <FormLabel className={classes.label}>
                        {t('ICD-10')}
                    </FormLabel>

                    <div>
                        <IconButton
                            disableRipple
                            disabled={disabled}
                            color="primary"
                        >
                            <AddCircleOutlineIcon/>
                        </IconButton>
                    </div>
                </div>

                {(showWarning && autocompleteOpen) &&
                    <div className={classes.warning}>
                        {t('Please enter an text that is at least 3 characters long')}
                    </div>
                }

                {(!_.isEmpty(innerValue) || autocompleteOpen) &&
                    <AgyleDictionaryField
                        focusInput={setFocus}
                        multiple
                        open={autocompleteOpen}
                        openOnFocus
                        onOpen={handleAutocompleteState.bind(this, true)}
                        onClose={handleAutocompleteState.bind(this, false)}
                        onBlur={handleAutocompleteState.bind(this, false)}
                        disabled={disabled}
                        isOptionEqualToValue={(option, value) => option?.__id === value?.__id}
                        options={autocompleteOptions}
                        loading={autocompleteLoading}
                        filterOptions={(x) => x}
                        groupBy={opt => opt.group}
                        noOptionsText={!isEmpty(autocompleteInputValueDebounced) ? t('Nothing found') : t('Please enter at least one letter')}
                        loadingText={t('Loading data ...')}
                        onInputChange={(event, newInputValue) => setAutocompleteInputValue(newInputValue)}
                        value={innerValue}
                        onChange={handleChange}
                        disableClearable
                        extended
                        renderInput={(params) => (
                            <div style={{position: 'relative'}}>
                                <AgyleTextField
                                    {...params}
                                    textFieldClass={classes.textBackground}
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: params.InputProps.startAdornment,
                                        endAdornment: (
                                            <>
                                                {autocompleteLoading ?
                                                    <CircularProgress color="inherit" size={20}/> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />

                                {autocompleteLoading &&
                                    <CircularProgress style={{position: 'absolute', left: '50%', top: '15%'}}/>
                                }
                            </div>
                        )}
                        renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                                <Chip
                                    label={option?.value?.__description}
                                    style={{fontWeight: 'bold'}}
                                    {...getTagProps({index})}
                                    avatar={renderAvatar(option)}
                                />
                            ),
                        )}
                        renderOption={(props, option, {selected}) => renderLocalOption(option, props)}
                        ListboxComponent={(props, ref) => <VirtualizedList ref={ref} {...props} />}
                    />
                }
            </div>

            {isConfirmRemoveElementOpen &&
                <MessageBox
                    onClose={handleCloseConfirmRemove}
                    title={t('Confirmation')}
                    haveAccept={{yes: t('Yes'), no: t('No')}}
                    onAccept={handleRemove}
                    message={t(removeType.current === 'removeOption'
                        ? 'Are you sure you want to remove this entry?'
                        : 'Are you sure you want to clear all values?',
                    )}
                    open={isConfirmRemoveElementOpen}
                    warning
                />
            }
        </>
    );
};

export default ICd10Control;

const VirtualizedList = React.forwardRef(({children, ...props}, ref) => {
    return (
        <List
            ref={ref}
            {...props}
            itemData={children}
            height={400}
            itemCount={children.length}
            itemSize={35}
            width="100%"
        >
            {Row}
        </List>
    );
});

const Row = ({index, style, data}) => {
    return (
        <div style={style}>
            {data[index]}
        </div>
    );
};